import {
    AutoMode,
    Balance,
    CallSplit,
    Campaign,
    Category,
    CrisisAlert,
    DriveFileRenameOutline,
    Event,
    GridView,
    Inventory,
    MonetizationOn,
    MoveUp,
    ScatterPlot,
    Schedule,
    Storefront,
    SwapVert,
    TableChart,
    Timeline,
    TrendingDown,
    TrendingUp,
    ViewTimeline,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { GenderIcon } from './components/atoms/GenderIcon/GenderIcon';
import { StockManagementIcon } from './components/atoms/StockManagementIcon/StockManagementIcon';
import { ProjectedOptionCountGuard } from './components/organisms/ProjectedOptionCountGuard';
import { SelectPartnerGuard } from './components/organisms/SelectPartnerGuard/SelectPartnerGuard';
import { BuyingEventManagement } from './components/views/BuyingEventManagement/BuyingEventManagement';
import { CategorySplit } from './components/views/CategorySplit/CategorySplit';
import { ChainFurnitureZone } from './components/views/ChainFurnitureZone/ChainFurnitureZone';
import { ClusterManagementView } from './components/views/ClusterManagementView/ClusterManagementView';
import { DeliveryProfile } from './components/views/DeliveryProfile/DeliveryProfile';
import { GenderSplit } from './components/views/GenderSplit/GenderSplit';
import { InheritanceSettings } from './components/views/InheritanceSettings/InheritanceSettings';
import { InventoryMasterData } from './components/views/InventoryMasterData/InventoryMasterData';
import { MarkDown } from './components/views/MarkDown/MarkDown';
import { MarkUp } from './components/views/MarkUp/MarkUp';
import NoosShare from './components/views/NoosShare';
import { OpenToBuyLimit } from './components/views/OpenToBuyLimit/OpenToBuyLimit';
import { ProductLineSplit } from './components/views/ProductLineSplit/ProductLineSplit';
import { ProjectedOptionCount } from './components/views/ProjectedOptionCount/ProjectedOptionCount';
import { SalesCampaignReport } from './components/views/SalesCampaignReport/SalesCampaignReport';
import { SalesCampaigns } from './components/views/SalesCampaigns/SalesCampaigns';
import { SalesCampaignsPlanning } from './components/views/SalesCampaignsPlanning/SalesCampaignPlanning';
import { StockManagement } from './components/views/StockManagement/StockManagement';
import { StoreInventoryManagement } from './components/views/StoreInventoryManagement/StoreInventoryManagement';
import { StyleFamilyNameEditor } from './components/views/StyleFamilyNameEditor/StyleFamilyNameEditor';
import { StyleTimeline } from './components/views/StyleTimeline/StyleTimeline';
import { StyleTimelineSettings } from './components/views/StyleTimelineSettings/StyleTimelineSettings';
import { StyleWarnings } from './components/views/StyleWarnings/StyleWarnings';
import { Turnover } from './components/views/Turnover/Turnover';
import { WeekCover } from './components/views/WeekCover/WeekCover';
import { Module, Route, ScopeEnum } from './domain';
import { useIsUsingGender, useIsUsingProductline, useScope } from './hooks';
import { useIsAdmin } from './hooks/auth/useIsAdmin';
import { useIsSuperUser } from './hooks/auth/useIsSuperUser';
import { useIsUsingBuyingEventManagement } from './hooks/chain-settings/useIsUsingBuyingEventManagement';
import { useIsUsingDeliveryProfile } from './hooks/chain-settings/useIsUsingDeliveryProfile';
import { useIsUsingOpenToBuyLimit } from './hooks/chain-settings/useIsUsingOpenToBuyLimit';

export const useRoutes = () => {
    const productLineActive = useIsUsingProductline();
    const genderSplitActive = useIsUsingGender();

    const showDeliveryProfile = useIsUsingDeliveryProfile();
    const showBuyingEventManagement = useIsUsingBuyingEventManagement();
    const showOpenToBuyLimit = useIsUsingOpenToBuyLimit();

    const showProductLine = productLineActive;
    const showGenderSplit = genderSplitActive && !productLineActive;

    const scope = useScope();
    const isSuperUser = useIsSuperUser();
    const isAdmin = useIsAdmin();

    const routes = useMemo<readonly Route[]>(
        () => [
            {
                icon: <MonetizationOn />,
                title: 'Sales Budget',
                path: `/${Module.Turnover}`,
                component: <Turnover />,
                type: 'long-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <Category />,
                title: 'Productline Split',
                path: `/${Module.ProductLineSplit}`,
                component: <ProductLineSplit />,
                type: 'long-term',
                show: showProductLine,
                guards: [SelectPartnerGuard],
            },
            {
                icon: <GenderIcon />,
                title: 'Gender Split',
                path: `/${Module.GenderSplit}`,
                component: <GenderSplit />,
                type: 'long-term',
                show: showGenderSplit,
                guards: [SelectPartnerGuard],
            },
            {
                icon: <CallSplit />,
                title: 'Category Split',
                path: `/${Module.CategorySplit}`,
                component: <CategorySplit />,
                type: 'long-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <AutoMode />,
                title: 'NOOS Share',
                path: `/${Module.NoosShare}`,
                component: <NoosShare />,
                type: 'long-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <TrendingUp />,
                title: 'Buying Markup',
                path: `/${Module.Markup}`,
                component: <MarkUp />,
                type: 'long-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <Timeline />,
                title: 'Delivery Profile',
                path: `/${Module.DeliveryProfile}`,
                component: <DeliveryProfile />,
                type: 'long-term',
                show: showDeliveryProfile,
                guards: [SelectPartnerGuard],
            },
            {
                icon: <Balance />,
                title: 'Open To Buy Limit',
                path: `/${Module.OpenToBuyLimit}`,
                component: <OpenToBuyLimit />,
                type: 'long-term',
                show: showOpenToBuyLimit && scope === ScopeEnum.PARTNER,
                guards: [SelectPartnerGuard],
            },
            {
                icon: <Schedule />,
                title: 'Week Cover',
                path: `/${Module.WeekCover}`,
                component: <WeekCover />,
                type: 'short-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <TrendingDown />,
                title: 'Markdown',
                path: `/${Module.Markdown}`,
                component: <MarkDown />,
                type: 'short-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <StockManagementIcon />,
                title: 'Stock Management',
                path: `/${Module.StockManagement}`,
                component: <StockManagement />,
                type: 'short-term',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <ViewTimeline />,
                title: 'Style Timeline',
                path: `/${Module.StyleTimeline}`,
                component: <StyleTimeline />,
                type: 'range-planning',
                guards: [SelectPartnerGuard],
                show: scope === ScopeEnum.PARTNER,
            },
            {
                icon: <Storefront />,
                title: 'Store Inventory',
                path: `/${Module.StoreInventoryManagement}`,
                component: <StoreInventoryManagement />,
                type: 'range-planning',
                guards: [SelectPartnerGuard],
                show: scope === ScopeEnum.PARTNER && (isSuperUser || isAdmin),
            },
            {
                icon: <TableChart />,
                title: 'Projected Option Count',
                path: `/${Module.ProjectedOptionCount}`,
                component: <ProjectedOptionCount />,
                type: 'range-planning',
                guards: [SelectPartnerGuard, ProjectedOptionCountGuard],
                show: scope !== ScopeEnum.CLUSTER,
            },
            {
                icon: <CrisisAlert />,
                title: 'Style Warnings',
                path: `/${Module.StyleWarnings}`,
                component: <StyleWarnings />,
                type: 'range-planning',
                guards: [SelectPartnerGuard],
                show: scope !== ScopeEnum.CLUSTER,
            },
            {
                icon: <Campaign />,
                title: 'Sales & Campaigns',
                path: `/${Module.SalesCampaigns}`,
                component: <SalesCampaigns />,
                type: 'sale-planning',
                guards: [SelectPartnerGuard],
                show: scope === ScopeEnum.PARTNER,
                exactMatch: false,
            },
            {
                icon: null,
                title: 'Sales & Campaigns planning',
                path: `/${Module.SalesCampaignsPlanning}/:id`,
                component: <SalesCampaignsPlanning />,
                type: 'sale-planning',
                guards: [SelectPartnerGuard],
                show: false,
            },
            {
                icon: null,
                title: 'Sales campaign report',
                path: `/${Module.SalesCampaignsReport}/:id`,
                component: <SalesCampaignReport />,
                type: 'sale-planning',
                guards: [SelectPartnerGuard],
                show: false,
            },
            {
                icon: <ScatterPlot />,
                title: 'Cluster Management',
                path: `/settings/${Module.ClusterManagement}`,
                component: <ClusterManagementView />,
                type: 'setting',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <Event />,
                title: 'Buying Event Management',
                path: `/settings/${Module.BuyingEventManagement}`,
                component: <BuyingEventManagement />,
                type: 'setting',
                show: showBuyingEventManagement,
                guards: [],
            },
            {
                icon: <MoveUp />,
                title: 'Inheritance Settings',
                path: `/settings/${Module.InheritanceSettings}`,
                component: <InheritanceSettings />,
                type: 'setting',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <SwapVert />,
                title: 'Style Timeline Settings',
                path: `/settings/${Module.StyleTimelineSettings}`,
                component: <StyleTimelineSettings />,
                type: 'setting',
                guards: [SelectPartnerGuard],
            },
            {
                icon: <DriveFileRenameOutline />,
                title: 'Style family name editor',
                path: `/settings/${Module.StyleFamilyNameEditor}`,
                component: <StyleFamilyNameEditor />,
                type: 'setting',
                guards: [],
                show: isSuperUser || isAdmin,
            },
            {
                icon: <Inventory />,
                title: 'Inventory master data',
                path: `/settings/${Module.InventoryMasterData}`,
                component: <InventoryMasterData />,
                type: 'setting',
                guards: [],
                show: isSuperUser || isAdmin,
            },
            {
                icon: <GridView />,
                title: 'Inventory master data zones',
                path: `/settings/${Module.InventoryMasterDataZones}`,
                component: <ChainFurnitureZone />,
                type: 'setting',
                guards: [],
                show: isSuperUser || isAdmin,
            },
        ],
        [
            showProductLine,
            showGenderSplit,
            showDeliveryProfile,
            showOpenToBuyLimit,
            isSuperUser,
            isAdmin,
            scope,
            showBuyingEventManagement,
        ]
    );

    return routes;
};
